import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  IPaymentMethodV2,
  IPaymentMethodV2DataRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

const urlApiPaymentMethodV2 = `${urlBackend}api/v1/formapagov2/`;

///* tanStack query hooks

export const useFetchPaymentMethodsV2Params = (
  params?: getPaymentMethodsV2Props,
  enabled = true
) => {
  return useQuery({
    queryKey: ['paymentMethodsV2', ...Object.values(params ?? {})],
    queryFn: async () => {
      return await getPaymentMethodsV2(params);
    },
    enabled: enabled,
  });
};

export const useCreatePaymentMethodV2 = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPaymentMethodV2,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethodsV2'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Método de pago V2 creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePaymentMethodV2 = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePaymentMethodV2,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethodsV2'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Método de pago V2 actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemovePaymentMethodV2 = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removePaymentMethodV2,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethodsV2'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

export const useGetPaymentMethodV2 = (id: string) => {
  return useQuery({
    queryKey: ['paymentMethodV2', id],
    queryFn: async () => {
      return getPaymentMethodV2(id);
    },
    enabled: !!id,
  });
};

///* axios types
export type getPaymentMethodsV2Props = Partial<IPaymentMethodV2> & {
  page?: number;
  page_size?: number;
  filterByState?: boolean;
};

export type CreatePaymentMethodV2Props = Omit<IPaymentMethodV2, 'id_formapago_v2'>;

///* axios functions
export const getPaymentMethodsV2 = async (props?: getPaymentMethodsV2Props) => {
  const stateParams = { ...props };

  // filterByState - por defecto solo muestra activos
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IPaymentMethodV2DataRes>(
    `${urlApiPaymentMethodV2}?${queryParams}`,
    true
  );
};

export const getPaymentMethodV2 = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IPaymentMethodV2>(`${urlApiPaymentMethodV2}${id}/`, true);

  res.status === 'error' &&
    setAlert(true, 'Método de pago V2 no encontrado', 'error');

  return res;
};

export const createPaymentMethodV2 = async (data: CreatePaymentMethodV2Props) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<IPaymentMethodV2>(urlApiPaymentMethodV2, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updatePaymentMethodV2 = async ({
  id,
  data,
}: {
  id: string;
  data: CreatePaymentMethodV2Props;
}) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<IPaymentMethodV2>(
    `${urlApiPaymentMethodV2}${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removePaymentMethodV2 = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<IPaymentMethodV2>(
    `${urlApiPaymentMethodV2}${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
